import React, { Component } from "react";
import ReactDOM from "react-dom";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Icon } from "@culturehq/components";
import * as CHQ from "chq";

import { t, Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import NotificationItem from "./NotificationItem";

const pointIncrementFilter = ({ notificationType }: CHQ.Notification) => (
  notificationType !== "points_incremented"
);

type NotificationsProps = {
  unseenNotifications: CHQ.Notification[];
  seenNotifications: CHQ.Notification[];
  tabIndex: number;
};

const Notifications: React.FC<NotificationsProps> = ({
  unseenNotifications, seenNotifications, tabIndex
}) => {
  const unseen = unseenNotifications.filter(pointIncrementFilter);
  const seen = seenNotifications.filter(pointIncrementFilter);

  return (
    <div className="notification-drawer__notifications">
      {unseen.length < 1 && seen.length < 1 && (
        <p className="notification-drawer__empty">
          <Trans>No notifications to show.</Trans>
        </p>
      )}

      {unseen.length > 0 && <h2><Trans>New</Trans></h2>}
      {unseen.map(notification => (
        <NotificationItem key={notification.id} notification={notification} tabIndex={tabIndex} />
      ))}

      {seen.length > 0 && <h2><Trans>Seen</Trans></h2>}
      {seen.map(notification => (
        <NotificationItem key={notification.id} notification={notification} tabIndex={tabIndex} />
      ))}
    </div>
  );
};

type NotificationDrawerProps = RouteComponentProps & {
  onDrawerClose: () => void;
  open: boolean;
  seenNotifications: CHQ.Notification[];
  unseenNotifications: CHQ.Notification[];
};

class NotificationDrawer extends Component<NotificationDrawerProps, {}> {
  private drawerRef = React.createRef<HTMLDivElement>();

  private unlisten: null | (() => void) = null;

  componentDidMount() {
    const { history, onDrawerClose } = this.props;

    this.unlisten = history.listen(onDrawerClose);
    window.addEventListener("click", this.handleWindowClick);
  }

  componentDidUpdate(prevProps: NotificationDrawerProps) {
    const { open } = this.props;

    if (open !== prevProps.open) {
      const drawer = this.drawerRef.current;

      if (!drawer) {
        return;
      }

      if (open) {
        window.requestAnimationFrame(() => {
          drawer.classList.add("notification-drawer-show");
          drawer.removeAttribute("aria-hidden");
        });
      } else {
        drawer.classList.remove("notification-drawer-show");
        window.requestAnimationFrame(() => (
          drawer.classList.add("notification-drawer-hide")
        ));

        setTimeout(() => {
          drawer.classList.remove("notification-drawer-hide");
          drawer.setAttribute("aria-hidden", "true");
        }, 300);
      }
    }
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }

    window.removeEventListener("click", this.handleWindowClick);
  }

  handleWindowClick = (event: MouseEvent) => {
    const { open, onDrawerClose } = this.props;
    const drawer = this.drawerRef.current;

    if (open && drawer && event.target instanceof Element && !drawer.contains(event.target)) {
      onDrawerClose();
    }
  };

  render() {
    const { seenNotifications, unseenNotifications, onDrawerClose, open } = this.props;

    return ReactDOM.createPortal(
      <div ref={this.drawerRef} className="notification-drawer" aria-hidden>
        <div className="notification-drawer__header">
          <span className="notification-drawer__bell">
            <Icon icon="ios-bell-outline" />
          </span>
          <h1><Trans>Notifications</Trans></h1>
        </div>
        <button type="button" className="notification-drawer__close" onClick={onDrawerClose} aria-label={t("Close button")} tabIndex={-1}>
          <Icon icon="close" />
        </button>
        <ErrorBoundary>
          <Notifications
            seenNotifications={seenNotifications}
            unseenNotifications={unseenNotifications}
            tabIndex={open ? 0 : -1}
          />
        </ErrorBoundary>
      </div>,
      document.body
    );
  }
}

export default withRouter(NotificationDrawer);
